<script lang="ts" setup>
import { ref, watch } from 'vue'
import { Icon } from '@iconify/vue'

import Menu from 'primevue/menu'
import Button from 'primevue/button'
import Checkbox from 'primevue/checkbox'

interface FilterOption {
  value: string
  label: string
  id: string
}

interface FilterGroup {
  title: string
  key: string
  options: FilterOption[]
}

interface Props {
  filterGroups: FilterGroup[]
}

const props = defineProps<Props>()

const menu = ref()
const filters = ref<Record<string, string[]>>({})

const emit = defineEmits<{
  filterChange: [filters: Record<string, string[]>]
}>()

const initializeFilters = () => {
  const initialFilters: Record<string, string[]> = {}
  props.filterGroups.forEach(group => {
    initialFilters[group.key] = []
  })
  filters.value = initialFilters
}

const clearAllFilters = () => {
  props.filterGroups.forEach(group => {
    filters.value[group.key] = []
  })
}

const toggle = (event: Event) => {
  menu.value.toggle(event)
}

const applyFilter = () => {
  emit('filterChange', filters.value)
  menu.value.hide()
}

const getTotalActiveFilters = () => {
  return Object.values(filters.value).reduce((total, groupFilters) => total + groupFilters.length, 0)
}

watch(filters, (newFilters) => {
  emit('filterChange', newFilters)
}, { deep: true })

initializeFilters()
</script>

<template>
  <div>
    <Button
        type="button"
        @click="toggle"
        outlined
        severity="secondary"
        aria-haspopup="true"
        aria-controls="overlay_menu"
        class="gap-x-2"
    >
      <span class="border-l pl-1.5">Filter </span>
      <Icon icon="mage:filter" class="text-xl" />
      <span v-if="getTotalActiveFilters() > 0" class="bg-primary-500 text-white rounded-full px-2 py-0.5 text-xs ml-1">
        {{ getTotalActiveFilters() }}
      </span>
    </Button>

    <Menu ref="menu" id="overlay_menu" popup class="!pb-0 min-w-[600px]">
      <template #start>
        <div
            class="p-3 border-b border-neutral-200 bg-neutral-100 flex items-center justify-between"
        >
          <span class="font-semibold">Filter Options</span>
          <div class="flex items-center gap-x-2 text-xs">
            <Button class="!p-1 !rounded-md" outlined severity="secondary" @click="clearAllFilters">
              Clear All
            </Button>
            <Button class="!p-1 !rounded-md" outlined @click="applyFilter">Apply</Button>
          </div>
        </div>
      </template>

      <template #end>
        <div class="max-h-96 overflow-y-auto">
          <div class="flex">
            <div
                v-for="(group, groupIndex) in filterGroups"
                :key="group.key"
                class="flex-1 border-r border-neutral-100 last:border-r-0"
            >
              <div class="px-3 py-2 bg-neutral-50 font-medium text-sm text-neutral-700 border-b border-neutral-100">
                {{ group.title }}
              </div>
              <ul class="flex flex-col text-[13px]">
                <li
                    v-for="option in group.options"
                    :key="option.value"
                    :class="`flex align-items-center gap-x-3 p-2 px-4 hover:bg-neutral-50 ${filters[group.key]?.includes(option.value) && 'bg-primary-50'}`"
                >
                  <Checkbox
                      :inputId="option.id"
                      :name="group.key"
                      :value="option.value"
                      v-model="filters[group.key]"
                  />
                  <label
                      :for="option.id"
                      class="font-medium translate-y-1 cursor-pointer flex-1"
                  >
                    {{ option.label }}
                  </label>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </template>
    </Menu>
  </div>
</template>

<style></style>
